'use client';

/* eslint-disable react-refresh/only-export-components */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReceiptIcon } from '@phosphor-icons/react';
import { useSWRConfig } from 'swr';
import { toast } from 'sonner';

import { track } from '@/lib/telemetry';
import { notifyError } from '@/lib/notify-error';
import { useFinance } from '@/hooks/useFinance';
import { useAccounts } from '@/hooks/queries/useAccounts';
import {
  CREDIT_COCKPIT_KEY_PREFIXES,
  RECURRING_PAYMENT_KEY_PREFIXES,
  swrKeyStartsWith,
  useAuthedFetcher,
} from '@/hooks/queries/swrConfig';
import { useCreditCockpit } from '@/hooks/queries/useCreditCockpit';
import { useCustomCategories } from '@/hooks/queries/useCustomCategories';
import { useTransactionsInfinite, useTransactionsMeta } from '@/hooks/queries/useTransactionsPage';
import { useTags } from '@/hooks/queries/useTags';
import { revalidateSpaceSending, revalidateTagCatalog, revalidateTransactionCaches } from '@/hooks/queries/swrConfig';
import { revalidatePostRefundCaches, useReviewQueue } from '@/hooks/queries/useReviewQueue';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { normalizeForSearch } from '@/lib/utils';
import { resolveCategoryColor } from '@/lib/transactions/categories';
import {
  type CategoryIndex,
  UNCATEGORIZED_META,
  buildCategoryIndexFromServerMeta,
} from './categoryIndex';
import {
  computeSummary,
  groupByDay,
  withDayTotals,
} from '@/lib/transactions/utils';
import type { Breakdown } from '@/lib/transactions/utils';
import type { PaymentType, RefundFacet, RefundLink, Transaction as DomainTransaction } from '@/lib/transactions/types';
import { ApiError, categoryBudgetApi, refundsApi, tagsApi, transactionsApi } from '@/lib/api';
import type { TransactionTag } from '@/types';
import type { ExpenseTransaction, InvoiceDetailResponse, TransactionRefundCandidate } from '@/lib/api';
import type { RuleAnchorOption } from '@/components/categories/ruleAnchor';
import type { TransactionDetails } from '@/types';
import {
  applyExpectedRefundPatch,
  applyRefundLinkPatch,
  clearRefundLinkPatch,
  heldAffectedIds,
  patchStandaloneTransaction,
} from '@/lib/transactions/refundOptimistic';
import {
  canFlagExpectedRefund,
  hasReportingAmount,
  reportingAmount,
  refundLinkToastMessage,
  refundUnlinkToastMessage,
  totalRefundedAmount,
  uncoveredAmount,
} from '@/lib/transactions/refunds';
import type { RefundLinkPatchInput, RefundPairSide } from '@/lib/transactions/refundOptimistic';
import { notifySpaceSendingChanged, useRereadOnSpaceWrite } from '@/lib/spaces/sendingChanges';
import type { PeriodSelection } from '@/lib/period-presets';
import type { ReviewQueueItem, Transaction as VerticeTransaction } from '@/types';
import { resolveRefundPair, reviewItemKey } from '@/components/orcamento/helpers';

import { MarkRecurringSheet, type MarkRecurringOptions } from '@/components/transactions/MarkRecurringSheet';
import { RefundLinkPickerDrawer, type RefundPickerSubject } from '@/components/transactions/RefundLinkPickerDrawer';
import { useTransactionsScreenState } from './useTransactionsScreenState';
import { SearchBar } from './SearchBar';
import { FilterChipsRow } from './FilterChipsRow';
import { SortControl } from './SortControl';
import { DayHeader } from './DayHeader';
import { TransactionRow } from './TransactionRow';
import { AccountPickerDrawer } from './AccountPickerDrawer';
import { PaymentMethodPickerDrawer } from './PaymentMethodPickerDrawer';
import { PlatformPickerDrawer } from './PlatformPickerDrawer';
import { RecurrencePickerDrawer } from './RecurrencePickerDrawer';
import { DirectionPickerDrawer } from './DirectionPickerDrawer';
import { RefundFacetPickerDrawer } from './RefundFacetPickerDrawer';
import { RefundReviewCard } from './RefundReviewCard';
import { TransactionDetailDrawer } from './TransactionDetailDrawer';
import { CategoryPickerDrawer } from '@/components/categories/CategoryPickerDrawer';
import { buildRuleAnchors, type RuleSaveOptions } from '@/components/categories/ruleAnchor';
import { retroApplyFeedback } from '@/components/categories/confirmCategoryFeedback';
import { useTransactionDetails } from '@/hooks/queries/useTransactionDetails';
import type { CategoryRuleTarget } from '@/types/category-budget';
import { CategoryFilterDrawer } from './CategoryFilterDrawer';
import { TagFilterDrawer } from './TagFilterDrawer';
import { TagAssignDrawer, type TagAssignSubject } from './TagAssignDrawer';
import { TotalCard } from './TotalCard';
import { CategoryBreakdownCard } from './CategoryBreakdownCard';
import { buildParentCategoryRows } from '../category-detail/buildCategoryRows';
import { FaturaDetailDrawer } from './FaturaDetailDrawer';
import { PageHeader } from '@/components/PageHeader';
import { PeriodPicker } from '@/components/PeriodPicker';
import { usePeriodSelection } from '@/hooks/usePeriodSelection';
import { adaptInvoiceToFatura, appendInvoiceDetailPage, invoiceAfterReread, type InvoicePageRead, markInvoicePaidInCockpit, parseInvoiceDetailTarget, reloadInvoiceDetail, toInvoicePaidKey } from './adapters';
import { TransactionRowSkeleton } from './TransactionRowSkeleton';
import { TransactionListCard, TransactionListEmpty } from '@/components/transactions/TransactionListCard';
import type { AccountOption, DrawerKind, ScreenFatura, ScreenTransaction } from './types';

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Categorizations tend to come in bursts; one trailing revalidation of the heavy
// snapshot/recurring caches replaces N cockpit rebuild+poll cycles.
const CATEGORY_SAVE_REVALIDATE_DEBOUNCE_MS = 2_500;

function adaptPaymentType(raw?: string | null): PaymentType {
  if (!raw) return 'other';
  const v = raw.toLowerCase();
  if (v.includes('pix')) return 'pix';
  if (v.includes('credit')) return 'credit';
  if (v.includes('debit')) return 'debit';
  if (v.includes('transfer') || v === 'ted' || v === 'doc') return 'transfer';
  if (v.includes('boleto')) return 'boleto';
  return 'other';
}

// A custom category overrides the Pluggy one and is both leaf and parent of its own
// single-node tree (same precedence the backend list/meta and the Categorias page use).
function deriveCategoryId(tx: VerticeTransaction): string {
  if (tx.customCategoryId) return tx.customCategoryId;
  if (tx.categoryId) return tx.categoryId;
  return 'uncategorized';
}

function deriveParentCategoryId(tx: VerticeTransaction): string {
  if (tx.customCategoryId) return tx.customCategoryId;
  if (tx.parentCategoryId) return tx.parentCategoryId;
  if (tx.categoryId) return tx.categoryId;
  return 'uncategorized';
}

function transactionPeriodDate(tx: VerticeTransaction): string {
  return tx.referenceDate ?? tx.effectiveDate ?? tx.date;
}

// How many later parcelas a refund link on this EXPENSE will also cover (server-side
// propagation across the installment series). Only meaningful for an installment expense.
function subsequentInstallmentsOf(tx: DomainTransaction): number {
  const amount = reportingAmount(tx);
  if (amount == null || amount >= 0 || !tx.installment) return 0;
  return Math.max(0, tx.installment.total - tx.installment.current);
}

function toDomainRefundLink(link: NonNullable<VerticeTransaction['refundLink']>): RefundLink {
  return {
    linkId: link.linkId,
    role: link.role,
    counterpartTransactionId: link.counterpartTransactionId,
    counterpartDescription: link.counterpartDescription,
    counterpartMerchantName: link.counterpartMerchantName ?? undefined,
    counterpartAmount: link.counterpartAmount,
    counterpartDate: link.counterpartDate,
    counterpartAccountName: link.counterpartAccountName,
    linkedAmount: link.linkedAmount,
    createdAt: link.createdAt,
    isSeriesLink: link.isSeriesLink ?? false,
  };
}

export function adaptTransactionForScreen(tx: VerticeTransaction): ScreenTransaction {
  const inferredPayment = adaptPaymentType(
    tx.paymentMethod ?? (tx.account?.type === 'CREDIT' ? 'credit' : 'debit'),
  );
  const installment = tx.isInstallment && tx.installmentNumber && tx.installmentTotal
    ? { current: tx.installmentNumber, total: tx.installmentTotal }
    : undefined;
  const originalMoney = tx.money?.original ?? {
    amount: tx.amount,
    currencyCode: tx.currencyCode || 'BRL',
  };
  const accountMoney = tx.money?.account ?? originalMoney;
  const reportingMoney = tx.money?.reporting;
  const signed = (amount: number) => tx.isExpense ? -Math.abs(amount) : Math.abs(amount);
  const signedAmount = signed(accountMoney.amount);
  const signedOriginalAmount = signed(originalMoney.amount);
  const hasDistinctOriginal = originalMoney.currencyCode !== accountMoney.currencyCode
    || Math.abs(Math.abs(originalMoney.amount) - Math.abs(accountMoney.amount)) > 0.00005;
  const domain: DomainTransaction = {
    id: tx.id,
    date: transactionPeriodDate(tx),
    // The user's own rename outranks every derived name (VER-377) — the original
    // description stays on tx.source for the drawer's "Descrição original".
    merchant: tx.customName ?? tx.merchantName ?? tx.aiRefinedDescription ?? tx.description,
    tags: tx.tags,
    renamed: tx.customName != null,
    amount: signedAmount,
    currencyCode: accountMoney.currencyCode,
    originalMoney: hasDistinctOriginal
      ? { amount: signedOriginalAmount, currencyCode: originalMoney.currencyCode }
      : undefined,
    reportingAmount: reportingMoney ? signed(reportingMoney.amount) : undefined,
    reportingCurrencyCode: reportingMoney?.currencyCode,
    conversionStatus: tx.money?.conversionStatus,
    reportingStatus: tx.money?.reportingStatus,
    accountId: tx.accountId,
    categoryId: deriveCategoryId(tx),
    parentCategoryId: deriveParentCategoryId(tx),
    paymentType: inferredPayment,
    isInternalTransfer: tx.isInternalTransfer ?? false,
    isCashFlowNeutral: tx.isCashFlowNeutral ?? tx.isInternalTransfer ?? false,
    billId: tx.billId ?? undefined,
    installment,
    refundedAmount: tx.refundedAmount && tx.refundedAmount > 0 ? tx.refundedAmount : undefined,
    refundLink: tx.refundLink ? toDomainRefundLink(tx.refundLink) : undefined,
    refundLinks: tx.refundLinks && tx.refundLinks.length > 0
      ? tx.refundLinks.map(toDomainRefundLink)
      : undefined,
    expectedRefund: tx.expectedRefundFlaggedAt
      ? { flaggedAt: tx.expectedRefundFlaggedAt, expiredAt: tx.expectedRefundExpiredAt ?? null }
      : undefined,
    spaceExpenses: tx.spaceExpenses?.length ? tx.spaceExpenses : undefined,
  };
  return { ...domain, source: tx };
}

// Referentially stable "no parent-category filter" value for CategoryFilterDrawer.
// A fresh [] per render would retrigger the drawer's [open, value] sync effect on
// every host re-render while it is open (exact child filters render with no parent
// selection), wiping pending selections mid-use.
const EMPTY_CATEGORY_FILTER: string[] = [];

// Canonical implementation moved to adapters.ts (shared with FaturasScreen);
// re-exported here to keep this module's public surface stable.
export { parseInvoiceDetailTarget } from './adapters';

function adaptExpenseTransaction(tx: ExpenseTransaction): DomainTransaction {
  const categoryId = tx.categoryId || 'uncategorized';
  const parentCategoryId = tx.parentCategoryId || tx.categoryId || 'uncategorized';
  return {
    id: tx.id,
    date: tx.referenceDate ?? tx.effectiveDate ?? tx.date,
    merchant: tx.aiRefinement ?? tx.description,
    isExpected: tx.isExpected,
    amount: tx.isRefund ? Math.abs(tx.value) : -Math.abs(tx.value),
    currencyCode: 'BRL',
    reportingAmount: tx.isRefund ? Math.abs(tx.value) : -Math.abs(tx.value),
    reportingCurrencyCode: 'BRL',
    accountId: tx.accountId,
    categoryId,
    parentCategoryId,
    paymentType: tx.method,
    installment: tx.parcelaInfo
      ? { current: tx.parcelaInfo.current, total: tx.parcelaInfo.total }
      : undefined,
    spaceExpenses: tx.spaceExpenses?.length ? tx.spaceExpenses : undefined,
  };
}

function _searchPredicate(tx: ScreenTransaction, q: string, chipLabel: string): boolean {
  const needle = normalizeForSearch(q.trim());
  if (!needle) return true;
  if (normalizeForSearch(tx.merchant).includes(needle)) return true;
  if (normalizeForSearch(chipLabel).includes(needle)) return true;
  if (tx.notes && normalizeForSearch(tx.notes).includes(needle)) return true;
  if (tx.source.description && normalizeForSearch(tx.source.description).includes(needle)) return true;
  return false;
}

export function nextCategoryChartFilter(
  currentCategoryIds: readonly string[],
  categoryId: string,
): string[] | null {
  return currentCategoryIds.length === 1 && currentCategoryIds[0] === categoryId
    ? null
    : [categoryId];
}

export type CategoryFilterPatch = {
  categoryIds: string[] | null;
  categoryFilterMode: 'parent' | 'exact' | null;
};

export function nextParentCategoryFilterPatch(
  currentCategoryIds: readonly string[],
  parentCategoryId: string,
): CategoryFilterPatch {
  const categoryIds = nextCategoryChartFilter(currentCategoryIds, parentCategoryId);
  return {
    categoryIds,
    categoryFilterMode: categoryIds ? 'parent' : null,
  };
}

export function exactCategoryFilterPatch(categoryId: string): CategoryFilterPatch {
  return {
    categoryIds: [categoryId],
    categoryFilterMode: 'exact',
  };
}

export function replaceTransactionById(
  transactions: readonly VerticeTransaction[],
  updated: VerticeTransaction,
): VerticeTransaction[] {
  return transactions.map((tx) => (tx.id === updated.id ? updated : tx));
}

export function replaceTransactionsById(
  transactions: readonly VerticeTransaction[],
  updatedTransactions: readonly VerticeTransaction[],
): VerticeTransaction[] {
  if (updatedTransactions.length === 0) return [...transactions];
  const byId = new Map(updatedTransactions.map((tx) => [tx.id, tx]));
  return transactions.map((tx) => byId.get(tx.id) ?? tx);
}

function TransactionsOverviewSkeleton() {
  return (
    <div
      data-testid="transactions-summary-row"
      role="status"
      aria-label="Carregando resumo de transações"
      className="grid gap-4 lg:grid-cols-2 lg:items-start lg:gap-6"
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
        {[0, 1, 2].map((item) => (
          <div
            key={item}
            className="h-32 animate-pulse rounded-2xl border border-border-subtle bg-surface-sunken sm:h-36"
          />
        ))}
      </div>
      <div className="h-60 animate-pulse rounded-2xl border border-border-subtle bg-surface-sunken" />
    </div>
  );
}

/**
 * The picker's rule wiring for one subject, kept pure so the host's contribution is
 * testable without rendering the screen: which anchors exist, whether they are still
 * being resolved, and — only once they are — the `ruleCreation` config.
 *
 * `ruleCreation` stays UNDEFINED while the details request is in flight. The drawer reads
 * any truthy config as `allowUnchangedCategory`, which enables Salvar for the category the
 * transaction already has; handed that config with no anchors, the user could save a no-op
 * PATCH and be told it worked, with no rule control ever on screen to explain why.
 */
export function buildCategoryPickerRuleConfig(
  transaction: ScreenTransaction | null,
  details: TransactionDetails | undefined,
): {
  anchors: RuleAnchorOption[];
  anchorsPending: boolean;
  ruleCreation?: { anchors: RuleAnchorOption[] };
} {
  if (!transaction) return { anchors: [], anchorsPending: false };

  // Details belonging to a PREVIOUS subject are not this subject's answer: the drawer can
  // be re-pointed without closing, and SWR keeps the old payload meanwhile.
  const resolved = details?.id === transaction.id ? details : undefined;
  if (!resolved) return { anchors: [], anchorsPending: true };

  const anchors = buildRuleAnchors({
    description: transaction.source.description,
    merchant: transaction.merchant,
    priorCount: resolved.priorCount ?? 0,
    establishment: resolved.establishment,
    merchantPriorCount: resolved.merchantPriorCount,
  });

  // The rule affordance belongs to ESTABLISHMENTS. A person counterparty has no merchants
  // row by construction, so offering "Sempre categorizar 'Renata Santos' como…" on this
  // screen changed how personal transactions read — a control that was never there before
  // and was never meant to be. The review queue is a different surface with a different
  // contract: it passes its own ruleCreation and still offers the descriptor rule there.
  const hasEstablishment = anchors.some((anchor) => anchor.kind === 'merchant');
  return {
    anchors,
    anchorsPending: false,
    ruleCreation: hasEstablishment ? { anchors } : undefined,
  };
}

/**
 * Whether a save has to go through the confirm endpoint. `PATCH /transactions` cannot
 * create a rule, so anything that asks for one — or for the retro-apply that implies one —
 * belongs on the path that can.
 */
export function saveNeedsRuleEndpoint(ruleOptions?: RuleSaveOptions): boolean {
  return !!(ruleOptions?.createRule || ruleOptions?.applyToMerchant);
}

export function TransactionsScreen() {
  const {
    fetchInvoiceDetail,
    markClosedInvoicePaid,
    updateTransaction,
    previewTransactionCategoryBatch,
    updateTransactionCategoryBatch,
  } = useFinance();
  const { mutate: mutateGlobal } = useSWRConfig();
  const authedFetch = useAuthedFetcher();
  const state = useTransactionsScreenState();
  // Period is URL+localStorage-backed (usePeriodSelection). VER-260 relocates the picker
  // from the global header into this screen's filter group, so we need onChange here too.
  const { value: periodValue, onChange: onPeriodChange } = usePeriodSelection();

  const [activeDrawer, setActiveDrawer] = useState<DrawerKind>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  // The FULL transaction, not an id: the category drawer's detail view fetches its
  // own category page, so a tapped transaction may not exist in this screen's
  // paginated list — an id-only lookup would open an empty drawer.
  const [detailTx, setDetailTx] = useState<ScreenTransaction | null>(null);
  // Single-level stack for counterpart navigation: the refund pair's other side
  // opens stacked over the current detail, and the back arrow restores it.
  const [detailTxOrigin, setDetailTxOrigin] = useState<ScreenTransaction | null>(null);
  // Tags + rename (VER-377/378).
  const [tagPickerTransactionId, setTagPickerTransactionId] = useState<string | null>(null);
  const [savingTagsTransactionId, setSavingTagsTransactionId] = useState<string | null>(null);
  const [isRenameSaving, setIsRenameSaving] = useState(false);
  // Row-level rename opens the detail drawer with the editor already armed.
  const [detailStartInRename, setDetailStartInRename] = useState(false);
  const [categoryPickerTransactionId, setCategoryPickerTransactionId] = useState<string | null>(null);
  const [savingCategoryTransactionId, setSavingCategoryTransactionId] = useState<string | null>(null);
  const [savingRecurringTransactionId, setSavingRecurringTransactionId] = useState<string | null>(null);
  // `mode` decides ADD vs ALTER: both sides fan out now, so picking an estorno for an expense
  // that already has one must CREATE a second link. Only the drawer's explicit "Alterar
  // vínculo" re-points the existing link.
  const [refundPicker, setRefundPicker] = useState<
    { id: string; source: 'transactions' | 'detail'; mode: 'add' | 'relink' } | null
  >(null);
  const [savingRefundTransactionId, setSavingRefundTransactionId] = useState<string | null>(null);
  // Refund review slice (the "A revisar" card above the breakdown).
  const [reviewPicking, setReviewPicking] = useState<ReviewQueueItem | null>(null);
  const [reviewBusyKey, setReviewBusyKey] = useState<string | null>(null);
  const [markRecurringTarget, setMarkRecurringTarget] = useState<{
    id: string;
    merchant: string | null;
    defaultKind: 'Subscription' | 'Utility';
  } | null>(null);
  const [selectedFaturaId, setSelectedFaturaId] = useState<string | null>(null);
  const [faturaDetail, setFaturaDetail] = useState<InvoiceDetailResponse | null>(null);
  const [isFaturaDetailLoading, setIsFaturaDetailLoading] = useState(false);
  const [isFaturaLoadingMore, setIsFaturaLoadingMore] = useState(false);
  const [isMarkingFaturaPaid, setIsMarkingFaturaPaid] = useState(false);
  const PAGE_SIZE = 50;
  const INVOICE_PAGE_SIZE = 50;

  const cockpitPeriod = useMemo<PeriodSelection>(() => ({
    preset: state.period,
    from: state.dateRange.from,
    to: state.dateRange.to,
  }), [state.period, state.dateRange]);
  const cockpitRequest = useCreditCockpit(cockpitPeriod);
  const cockpit = cockpitRequest.data ?? null;
  const isCockpitLoading = cockpitRequest.isLoading && !cockpit;

  const sortField: 'date' | 'value' | 'merchant' =
    state.sort === 'date' ? 'date' : state.sort === 'amount' ? 'value' : 'merchant';

  const transactionQuery = useMemo(() => ({
    from: state.dateRange.from,
    to: state.dateRange.to,
    accountId: state.account ?? undefined,
    paymentMethod: state.paymentMethod ?? undefined,
    platform: state.platform ?? undefined,
    // Direction (single) → income/expense; Recorrência (multi) → the recurringGroup[] tokens.
    isBankIncome: state.direction === 'entradas' ? true : undefined,
    isExpense: state.direction === 'saidas' ? true : undefined,
    recurringGroup: state.recurrence.length > 0 ? state.recurrence : undefined,
    categoryId: state.categoryFilterMode === 'exact' && state.categoryIds.length > 0
      ? state.categoryIds
      : undefined,
    parentCategoryId: state.categoryFilterMode !== 'exact' && state.categoryIds.length > 0
      ? state.categoryIds
      : undefined,
    tagIds: state.tagIds.length > 0 ? state.tagIds : undefined,
    refund: state.refund ?? undefined,
    q: state.q.trim() || undefined,
    sortField,
    sortOrder: state.order,
    limit: PAGE_SIZE,
  }), [
    state.dateRange,
    state.account,
    state.paymentMethod,
    state.platform,
    state.direction,
    state.recurrence,
    state.tagIds,
    state.categoryIds,
    state.categoryFilterMode,
    state.refund,
    state.q,
    sortField,
    state.order,
  ]);

  const {
    data: transactionPages,
    isLoading: isTransactionsLoading,
    isValidating: isTransactionsValidating,
    size: transactionPageCount,
    setSize: setTransactionPageCount,
    mutate: mutateTransactionPages,
  } = useTransactionsInfinite(transactionQuery);

  const {
    data: transactionsMeta,
    isLoading: isTransactionsMetaLoading,
    mutate: mutateTransactionsMeta,
  } = useTransactionsMeta(transactionQuery);

  const { data: accounts = [], isLoading: isAccountsLoading } = useAccounts();

  const { data: tagCatalog = [] } = useTags();

  const tagFacets = transactionsMeta?.tagFacets;
  const tagCountsByTagId = useMemo(
    () => new Map((tagFacets ?? []).map((facet) => [facet.id, facet.count])),
    [tagFacets],
  );
  const tagAmountsByTagId = useMemo(
    () => new Map((tagFacets ?? []).map((facet) => [facet.id, facet.amount])),
    [tagFacets],
  );

  // Refund review items live on THIS page; /budget keeps the category slice.
  const { data: refundReviewQueue, mutate: mutateRefundReviewQueue } = useReviewQueue('refund');

  const transactions = useMemo(
    () => transactionPages?.flatMap((page) => page.items) ?? [],
    [transactionPages],
  );

  const lastTransactionPage = transactionPages?.[transactionPages.length - 1] ?? null;
  const hasLoadedTransactionPages = Array.isArray(transactionPages);
  const hasLoadedTransactionMeta = Boolean(transactionsMeta);
  const isInitialPageLoading =
    (!hasLoadedTransactionPages && isTransactionsLoading) ||
    (!hasLoadedTransactionMeta && isTransactionsMetaLoading) ||
    (isAccountsLoading && accounts.length === 0) ||
    isCockpitLoading;

  const accountOptions: AccountOption[] = useMemo(
    () => accounts.map((a) => {
      const bank = a.item?.connectorName ?? a.name;
      const last4 = (a.number ?? '').replace(/\D/g, '').slice(-4);
      const kind: AccountOption['type'] = a.type === 'CREDIT'
        ? 'credit'
        : (a as { subtype?: string }).subtype && /INVEST/i.test((a as { subtype?: string }).subtype!)
          ? 'investment'
          : 'checking';
      // Variant label: tail of `account.name` after the bank name, when short.
      const remainder = a.name.replace(new RegExp(`^${escapeRegex(bank)}\\s*`, 'i'), '').trim();
      const label = remainder && remainder !== a.name && remainder.length <= 20 ? remainder : undefined;
      return {
        id: a.id,
        bank,
        type: kind,
        last4,
        label,
        creditBrand: a.creditBrand ?? null,
        connectorId: a.item?.connectorId ?? null,
        connectorImage: a.item?.connectorImage ?? null,
        connectorPrimaryColor: a.item?.connectorPrimaryColor ?? null,
        colorOverride: a.colorOverride ?? null,
      };
    }),
    [accounts],
  );

  const adapted: ScreenTransaction[] = useMemo(
    () => transactions.map(adaptTransactionForScreen),
    [transactions],
  );

  // Backend is the source of truth for category metadata. The shared builder
  // colors every ref through `resolveCategoryColor`, and the custom-category
  // overlay makes a user-picked color win over the palette for cuid refs —
  // previously /transactions hashed those and ignored the picked color.
  const { data: customCategories } = useCustomCategories(true);
  const categoryIndex: CategoryIndex = useMemo(
    () => buildCategoryIndexFromServerMeta(transactionsMeta, adapted, customCategories),
    [adapted, transactionsMeta, customCategories],
  );

  const visible = adapted;

  const summary = useMemo(
    () => transactionsMeta?.summary ?? computeSummary(visible),
    [transactionsMeta, visible],
  );

  const periodDays = useMemo(() => {
    const { from, to } = state.dateRange;
    if (!from || !to) return undefined;
    const ms = to.getTime() - from.getTime();
    return Math.max(1, Math.round(ms / 86400000) + 1);
  }, [state.dateRange]);

  // Categories present in the *period* (before category filter applies), so the
  // picker only offers options that would actually return rows. Built from
  // `searched` (search + suppression already applied) but ignoring the current
  // category/account/type filters — otherwise the picker would collapse to the
  // already-selected slice and hide every other option.
  const availableCategoryIds = useMemo(() => {
    const out = new Set<string>();
    for (const facet of transactionsMeta?.categoryFacets ?? []) {
      out.add(facet.parentCategoryId ?? facet.id);
    }
    if (out.size === 0) {
      for (const t of visible) out.add(t.parentCategoryId ?? t.categoryId);
    }
    return out;
  }, [transactionsMeta, visible]);

  // Accounts present in the period — same approach as `availableCategoryIds`.
  // Drives the Conta picker so users only see accounts that actually have
  // transactions in scope (plus whatever's currently selected, so it can be
  // deselected).
  const availableAccountIds = useMemo(() => {
    const out = new Set<string>();
    for (const facet of transactionsMeta?.accountFacets ?? []) out.add(facet.id);
    if (out.size === 0) {
      for (const t of visible) out.add(t.accountId);
    }
    return out;
  }, [transactionsMeta, visible]);

  // Payment methods present in the period — same approach as
  // `availableCategoryIds` / `availableAccountIds`. Drives the Forma de
  // pagamento picker so users only see methods that have transactions in scope.
  const availablePaymentMethods = useMemo(() => {
    const out = new Set<PaymentType>();
    for (const facet of transactionsMeta?.paymentMethodFacets ?? []) out.add(facet.id as PaymentType);
    if (out.size === 0) {
      for (const t of visible) out.add(t.paymentType);
    }
    return out;
  }, [transactionsMeta, visible]);

  // Purchase channels present in the period, with their counts — feeds the "Canal de
  // compra" picker (facet-driven like availablePaymentMethods; no client-side fallback:
  // platform is not on the list DTO, so the meta facets are the only source).
  const platformFacetCounts = useMemo(() => {
    const out = new Map<string, number>();
    for (const facet of transactionsMeta?.platformFacets ?? []) {
      // The picker treats key presence as "offer this channel", so a zero-count facet
      // must not land in the map (the API groups from existing rows and shouldn't send
      // one, but the picker's contract must not depend on that).
      if (facet.count > 0) out.set(facet.id, facet.count);
    }
    return out;
  }, [transactionsMeta]);

  const filteredAccountOptions = useMemo(() => {
    return accountOptions.filter(
      (a) => availableAccountIds.has(a.id) || a.id === state.account,
    );
  }, [accountOptions, availableAccountIds, state.account]);

  // Per-parent amounts/shares for the full category set. Sourced from
  // `categoryFacets` (the complete list) rather than `categoryBreakdown`
  // (top-5 only), so every non-neutral row in the picker shows its absolute
  // value and share, not just the top expense categories.
  const parentCategoryRows = useMemo(
    () => buildParentCategoryRows(transactionsMeta?.categoryFacets, categoryIndex),
    [transactionsMeta, categoryIndex],
  );

  const sharesByCategoryId = useMemo(() => {
    const out = new Map<string, number>();
    for (const row of parentCategoryRows) out.set(row.id, row.pct);
    return out;
  }, [parentCategoryRows]);

  const amountsByCategoryId = useMemo(() => {
    const out = new Map<string, number>();
    for (const row of parentCategoryRows) out.set(row.id, row.amount);
    return out;
  }, [parentCategoryRows]);

  const breakdownTop5 = useMemo<Breakdown>(() => {
    const source = transactionsMeta?.categoryBreakdown;
    const byCategoryId = new Map<string, number>();
    if (!source) {
      return { segments: [], total: 0, byCategoryId };
    }
    for (const segment of source.segments) byCategoryId.set(segment.categoryId, segment.total);
    return {
      segments: source.segments,
      total: source.total,
      byCategoryId,
    };
  }, [transactionsMeta]);

  const categoryFilterIdByCategoryId = useMemo(() => {
    const map = new Map<string, string>();
    for (const meta of transactionsMeta?.categories ?? []) {
      map.set(meta.id, meta.parentCategoryId ?? meta.id);
    }
    for (const facet of transactionsMeta?.categoryFacets ?? []) {
      map.set(facet.id, facet.parentCategoryId ?? facet.id);
    }
    for (const t of adapted) {
      map.set(t.categoryId, t.parentCategoryId ?? t.categoryId);
    }
    return map;
  }, [adapted, transactionsMeta]);

  const handleSelectChartCategory = (categoryId: string) => {
    const filterId = categoryFilterIdByCategoryId.get(categoryId) ?? categoryId;
    state.patch(nextParentCategoryFilterPatch(state.categoryIds, filterId));
  };

  const handleRefundDrawerOpenChange = (open: boolean) => setActiveDrawer(open ? 'refund' : null);
  const handleSelectRefundFacet = (value: RefundFacet) => state.patch({ refund: value });
  const handleClearRefundFacet = () => state.patch({ refund: null });

  // M4: faturas adapted from the cockpit response, scoped to the current period.
  // Membership semantics match the prototype: a fatura appears when its *cycle*
  // overlaps the selected period (not when its dueDate falls inside it — dueDates
  // are typically a month after the cycle, so dueDate containment dropped every
  // fatura whose cycle was the selected month).
  const baseVisibleTransactions = visible as ScreenTransaction[];
  /*
    // No client-side period overlap filter — the backend's cockpit endpoint
    // is now period-scoped (accepts from/to). A second filter here would only
    // re-interpret no-offset ISO strings as local time and risk dropping
    // border-of-the-range faturas.
    for (const account of cockpit.accounts) {
      for (const invoice of account.invoices) {
        out.push(adaptInvoiceToFatura(invoice, account, optionByAccountId.get(account.id)));
      }
    }
    return out;
  }, [cockpit, accountOptions]);

  // Recompute `transactionCount` per fatura from the actual client-side
  // transaction list — same predicate the detail drawer uses to resolve its
  // rows (billId match, or account + cycle membership). Without this, the
  // parent's "N lançamentos" comes from the backend cockpit (different
  // pipeline) and disagrees with the drawer's row count.
  const faturasWithCounts: ScreenFatura[] = useMemo(() => {
    if (faturas.length === 0) return faturas;
    return faturas.map((f) => {
      const billId = f.source.billId;
      let count = 0;
      if (billId) {
        for (const t of adapted) {
          if (t.source.billId === billId) count++;
        }
      } else {
        const start = f.cycleStart ? new Date(f.cycleStart).getTime() : -Infinity;
        const end = f.cycleEnd ? new Date(f.cycleEnd).getTime() : Infinity;
        for (const t of adapted) {
          if (t.accountId !== f.accountId) continue;
          const ts = new Date(t.date).getTime();
          if (ts >= start && ts <= end) count++;
        }
      }
      return count === f.transactionCount ? f : { ...f, transactionCount: count };
    });
  }, [faturas, adapted]);

  const faturaById = useMemo(
    () => new Map(faturasWithCounts.map((f) => [f.id, f])),
    [faturasWithCounts],
  );

  // Map credit transactions to their fatura by Vertice's `BillId` (Pluggy bill id).
  const faturaByVerticeBillId = useMemo(() => {
    const map = new Map<string, ScreenFatura>();
    for (const f of faturasWithCounts) {
      if (f.source.billId) map.set(f.source.billId, f);
    }
    return map;
  }, [faturasWithCounts]);

  // Faturas grouped by accountId, sorted so newer cycles come first — used as
  // a fallback resolver for credit-card transactions whose Pluggy `billId` is
  // null (common for the currently-open cycle).
  const faturasByAccountId = useMemo(() => {
    const map = new Map<string, ScreenFatura[]>();
    for (const f of faturasWithCounts) {
      const list = map.get(f.accountId) ?? [];
      list.push(f);
      map.set(f.accountId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (b.cycleStart || b.dueDate).localeCompare(a.cycleStart || a.dueDate));
    }
    return map;
  }, [faturasWithCounts]);

  // Override `faturaId` on transactions that belong to a fatura we know about,
  // so the row pill resolves. Two resolution strategies:
  //   1. billId match — preferred (closed cycles always have one).
  //   2. account + cycle range — fallback for null-billId open cycles.
  const visibleWithFaturaId: ScreenTransaction[] = useMemo(() => {
    const screen = visible as ScreenTransaction[];
    if (faturasWithCounts.length === 0) return screen;
    return screen.map((t) => {
      // Strategy 1: exact billId
      const billId = t.source.billId;
      if (billId) {
        const f = faturaByVerticeBillId.get(billId);
        if (f) return { ...t, faturaId: f.id };
      }
      // Strategy 2: account + cycle membership (credit accounts only)
      const isCredit = t.paymentType === 'credit' || t.source.account?.type === 'CREDIT';
      if (!isCredit) return t;
      const candidates = faturasByAccountId.get(t.accountId);
      if (!candidates || candidates.length === 0) return t;
      const txTime = new Date(t.source.date).getTime();
      const match = candidates.find((f) => {
        const start = f.cycleStart ? new Date(f.cycleStart).getTime() : -Infinity;
        const end = f.cycleEnd ? new Date(f.cycleEnd).getTime() : Infinity;
        return txTime >= start && txTime <= end;
      });
      if (!match) return t;
      return { ...t, faturaId: match.id };
    });
  }, [visible, faturasWithCounts, faturaByVerticeBillId, faturasByAccountId]);

  */

  const faturas: ScreenFatura[] = useMemo(() => {
    if (!cockpit) return [];
    const optionByAccountId = new Map(accountOptions.map((o) => [o.id, o]));
    const out: ScreenFatura[] = [];
    for (const account of cockpit.accounts) {
      for (const invoice of account.invoices) {
        out.push(adaptInvoiceToFatura(invoice, account, optionByAccountId.get(account.id)));
      }
    }
    return out;
  }, [cockpit, accountOptions]);

  // Trust the cockpit's `transactionCount` — the cockpit endpoint is the
  // authority for cycle membership. Client-side recomputation from `adapted`
  // would under-count whenever the active period filter is narrower than the
  // fatura's billing cycle.
  const faturasWithCounts: ScreenFatura[] = faturas;

  const faturaById = useMemo(
    () => new Map(faturasWithCounts.map((f) => [f.id, f])),
    [faturasWithCounts],
  );

  const faturaByVerticeBillId = useMemo(() => {
    const map = new Map<string, ScreenFatura>();
    for (const f of faturasWithCounts) {
      if (f.source.billId) map.set(f.source.billId, f);
    }
    return map;
  }, [faturasWithCounts]);

  const faturasByAccountId = useMemo(() => {
    const map = new Map<string, ScreenFatura[]>();
    for (const f of faturasWithCounts) {
      const list = map.get(f.accountId) ?? [];
      list.push(f);
      map.set(f.accountId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (b.cycleStart || b.dueDate).localeCompare(a.cycleStart || a.dueDate));
    }
    return map;
  }, [faturasWithCounts]);

  // Stamps the owning fatura onto a transaction — Vertice bill id first, then the
  // credit account's cycle window. Applied to the whole visible list AND to
  // category-drawer transactions that aren't in it (their copies come from a
  // separate query that carries no affiliation).
  const affiliateFatura = useCallback((t: ScreenTransaction): ScreenTransaction => {
    if (faturasWithCounts.length === 0) return t;
    const billId = t.source.billId;
    if (billId) {
      const f = faturaByVerticeBillId.get(billId);
      if (f) return { ...t, faturaId: f.id };
    }

    const isCredit = t.paymentType === 'credit' || t.source.account?.type === 'CREDIT';
    if (!isCredit) return t;
    const candidates = faturasByAccountId.get(t.accountId);
    if (!candidates || candidates.length === 0) return t;
    const txTime = new Date(t.source.date).getTime();
    const match = candidates.find((f) => {
      const start = f.cycleStart ? new Date(f.cycleStart).getTime() : -Infinity;
      const end = f.cycleEnd ? new Date(f.cycleEnd).getTime() : Infinity;
      return txTime >= start && txTime <= end;
    });
    return match ? { ...t, faturaId: match.id } : t;
  }, [faturasWithCounts, faturaByVerticeBillId, faturasByAccountId]);

  const visibleTransactions: ScreenTransaction[] = useMemo(() => {
    if (faturasWithCounts.length === 0) return baseVisibleTransactions;
    return baseVisibleTransactions.map(affiliateFatura);
  }, [baseVisibleTransactions, faturasWithCounts, affiliateFatura]);

  const closeTransactionDetails = () => {
    setDetailTx(null);
    setDetailTxOrigin(null);
    setDetailStartInRename(false);
  };
  // Main-list rows always come from visibleTransactions, so this lookup can't miss.
  const openTransactionDetailsById = (txId: string) => {
    track('action.transaction.open_detail', { source: 'list' });
    setDetailStartInRename(false);
    setDetailTxOrigin(null);
    setDetailTx(visibleTransactions.find((t) => t.id === txId) ?? null);
  };

  // What the detail drawer actually renders. `detailTx` is only identity + fallback
  // payload; the live list wins so the drawer tracks refreshed data, and the
  // fallback re-stamps its fatura affiliation against the CURRENT maps on every
  // change — a click-time stamp would go stale (or never happen) when the cockpit
  // resolves while the drawer is open (SWR keeps prior cockpit data during period changes).
  const resolvedDetailTx = useMemo(() => {
    if (!detailTx) return null;
    const live = visibleTransactions.find((t) => t.id === detailTx.id);
    if (live) return live;
    return affiliateFatura({ ...detailTx, faturaId: undefined });
  }, [detailTx, visibleTransactions, affiliateFatura]);
  // The row the drawer shows now, for a handler that awaits: a space write's reread may refresh it meanwhile.
  const resolvedDetailTxRef = useRef(resolvedDetailTx);
  useEffect(() => {
    resolvedDetailTxRef.current = resolvedDetailTx;
  });

  const categoryPickerTransaction = categoryPickerTransactionId
    ? visibleTransactions.find((t) => t.id === categoryPickerTransactionId) ?? null
    : null;
  const categoryPickerSubject = categoryPickerTransaction
    ? {
        id: categoryPickerTransaction.id,
        label: categoryPickerTransaction.merchant,
        currentCategoryId: categoryPickerTransaction.categoryId,
      }
    : null;

  // The curated identity for the ONE transaction the picker is open on. Fetched here
  // rather than carried on every list row: the merchant-grain prior count is a per-row
  // aggregate nobody looks at until this drawer opens.
  const { data: categoryPickerDetails } = useTransactionDetails(categoryPickerTransactionId);
  const categoryPickerRuleConfig = useMemo(
    () => buildCategoryPickerRuleConfig(categoryPickerTransaction, categoryPickerDetails),
    [categoryPickerTransaction, categoryPickerDetails],
  );
  const { anchors: categoryPickerAnchors, anchorsPending: categoryPickerAnchorsPending } =
    categoryPickerRuleConfig;

  const revalidateCreditCockpit = useCallback(() => mutateGlobal((key) =>
    swrKeyStartsWith(key, CREDIT_COCKPIT_KEY_PREFIXES)), [mutateGlobal]);
  const revalidateRecurringCaches = useCallback(() => mutateGlobal((key) =>
    swrKeyStartsWith(key, RECURRING_PAYMENT_KEY_PREFIXES)), [mutateGlobal]);
  // A tag or a category can be what one of the person's sending rules keys on, and the API evaluates those rules on
  // the edit (VER-444). The rows carry the spaces they are in (VER-571): every other read of them, and the rows held
  // outside SWR (the open invoice's charges, the drawer's), are read again with the heavy caches. The edited rows
  // themselves came back from the save.
  const revalidateHeavyCaches = useCallback(() => {
    void revalidateCreditCockpit();
    void revalidateRecurringCaches();
    revalidateTransactionCaches(mutateGlobal);
    notifySpaceSendingChanged();
  }, [mutateGlobal, revalidateCreditCockpit, revalidateRecurringCaches]);
  // Debounced: recomputing the on-demand cockpit, and reading the rows again, for every categorization makes rapid
  // category edits needlessly repeat the heavier queries.
  const scheduleHeavyRevalidation = useDebouncedCallback(
    revalidateHeavyCaches,
    CATEGORY_SAVE_REVALIDATE_DEBOUNCE_MS,
  );
  // The space reads (the send previews, the spaces' expenses) are read again at once, not behind the debounce.
  const revalidateAfterEdit = useCallback(() => {
    revalidateSpaceSending(mutateGlobal);
    scheduleHeavyRevalidation();
  }, [mutateGlobal, scheduleHeavyRevalidation]);

  const handleSaveTransactionCategory = async (
    txId: string,
    categoryId: string,
    source: 'pluggy' | 'custom' = 'pluggy',
    ruleOptions?: RuleSaveOptions,
  ) => {
    setSavingCategoryTransactionId(txId);
    try {
      // A rule is not something PATCH /transactions can create, so a request for one goes
      // through the same confirm endpoint the review queue uses — whose retro-apply
      // already follows the chosen anchor. Without a rule the plain update stays: it does
      // not flip categoryStatus or record feedback, which a bare recategorize should not.
      if (saveNeedsRuleEndpoint(ruleOptions)) {
        const result = await authedFetch((token) => categoryBudgetApi.confirmCategory(token, txId, {
          categoryRef: categoryId,
          createRule: ruleOptions?.createRule,
          applyToMerchant: ruleOptions?.applyToMerchant,
          ruleAnchor: ruleOptions?.ruleAnchor,
        }));
        const feedback = retroApplyFeedback(result);
        if (feedback) toast[feedback.severity](feedback.message);
        void mutateTransactionPages();
        void mutateTransactionsMeta();
        revalidateAfterEdit();
        setCategoryPickerTransactionId(null);
        track('action.categorize.apply', {
          source: 'transactions',
          mode: 'single',
          count: 1,
          rule_created: result.ruleCreated,
        });
        return;
      }

      const updated = await updateTransaction(
        txId,
        source === 'custom' ? { customCategoryId: categoryId } : { categoryId },
      );
      await mutateTransactionPages(
        (current) => current?.map((page) => ({
          ...page,
          items: replaceTransactionById(page.items, updated),
        })),
        { revalidate: false },
      );
      void mutateTransactionsMeta();
      revalidateAfterEdit();
      setCategoryPickerTransactionId(null);
      track('action.categorize.apply', {
        source: 'transactions',
        mode: 'single',
        count: 1,
        rule_created: false,
      });
      toast.success('Categoria atualizada');
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível atualizar a categoria');
      throw err;
    } finally {
      setSavingCategoryTransactionId(null);
    }
  };

  const handleMarkTransactionRecurring = (txId: string) => {
    const transaction = transactions.find((tx) => tx.id === txId);
    if (transaction && transaction.isExpense !== true) {
      toast.error('Somente despesas podem ser marcadas como recorrentes');
      return;
    }

    setMarkRecurringTarget({
      id: txId,
      merchant: transaction?.merchantName ?? transaction?.description ?? null,
      // Assinatura is an explicit choice, never a funding-source default — a posto on the
      // card is a conta fixa until the user says otherwise.
      defaultKind: 'Utility',
    });
  };

  const confirmMarkTransactionRecurring = async ({ kind, cadence }: MarkRecurringOptions) => {
    const target = markRecurringTarget;
    if (!target) return;
    setMarkRecurringTarget(null);

    setSavingRecurringTransactionId(target.id);
    await mutateTransactionPages(
      (current) => current?.map((page) => ({
        ...page,
        items: page.items.map((tx) => (
          tx.id === target.id ? { ...tx, isRecurring: true } : tx
        )),
      })),
      { revalidate: false },
    );

    try {
      const updated = await updateTransaction(target.id, {
        isRecurring: true,
        recurringCadence: cadence,
        recurringKind: kind,
      });
      await mutateTransactionPages(
        (current) => current?.map((page) => ({
          ...page,
          items: replaceTransactionById(page.items, updated),
        })),
        { revalidate: false },
      );
      void mutateTransactionsMeta();
      void revalidateCreditCockpit();
      void revalidateRecurringCaches();
      track('action.transaction.mark_recurring', { cadence, kind });
      toast.success(cadence === 'Yearly' ? 'Recorrência anual criada' : 'Transação marcada como recorrente');
    } catch (err) {
      void mutateTransactionPages();
      notifyError('transactions', err, 'Não foi possível marcar como recorrente');
    } finally {
      setSavingRecurringTransactionId(null);
    }
  };

  const handlePreviewTransactionCategoryBatch = useCallback((
    txId: string,
    categoryId: string,
    source: 'pluggy' | 'custom' = 'pluggy',
  ) =>
    previewTransactionCategoryBatch({
      sourceTransactionId: txId,
      ...(source === 'custom' ? { customCategoryId: categoryId } : { categoryId }),
    }), [previewTransactionCategoryBatch]);

  const handleSaveTransactionCategoryBatch = useCallback(async (
    txId: string,
    categoryId: string,
    source: 'pluggy' | 'custom' = 'pluggy',
    ruleAnchor?: CategoryRuleTarget,
  ) => {
    setSavingCategoryTransactionId(txId);
    try {
      const result = await updateTransactionCategoryBatch({
        sourceTransactionId: txId,
        ...(source === 'custom' ? { customCategoryId: categoryId } : { categoryId }),
        // The batch always mints a rule server-side; sending the anchor the user was shown
        // is what stops the server deciding its scope on their behalf.
        ruleAnchor,
      });

      if (result.updatedTransactions.length > 0) {
        await mutateTransactionPages(
          (current) => current?.map((page) => ({
            ...page,
            items: replaceTransactionsById(page.items, result.updatedTransactions),
          })),
          { revalidate: false },
        );
      }

      void mutateTransactionPages();
      void mutateTransactionsMeta();
      revalidateAfterEdit();
      setCategoryPickerTransactionId(null);

      track('action.categorize.apply', {
        source: 'transactions',
        mode: 'batch_same_description',
        count: result.updatedCount,
        // The batch ALWAYS mints a rule server-side. Reporting false here made this the
        // one categorization path whose telemetry contradicted what it did — and it is
        // the path that creates the most rules.
        rule_created: true,
      });
      if (result.failedCount > 0) {
        toast.warning(`${result.updatedCount} atualizadas, ${result.failedCount} falharam`);
      } else {
        toast.success(`${result.updatedCount} categorias atualizadas`);
      }
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível atualizar as categorias');
      throw err;
    } finally {
      setSavingCategoryTransactionId(null);
    }
  }, [
    updateTransactionCategoryBatch,
    mutateTransactionPages,
    mutateTransactionsMeta,
    revalidateAfterEdit,
  ]);

  // ── Linked refunds ──────────────────────────────────────────────────────────

  const refundPickerTransaction = refundPicker
    ? visibleTransactions.find((t) => t.id === refundPicker.id)
      ?? (resolvedDetailTx?.id === refundPicker.id ? resolvedDetailTx : null)
    : null;
  const refundPickerReportingAmount = refundPickerTransaction
    ? reportingAmount(refundPickerTransaction)
    : undefined;
  const refundPickerSubject: RefundPickerSubject | null = refundPickerTransaction
    && refundPickerReportingAmount != null
    ? {
        id: refundPickerTransaction.id,
        side: refundPickerReportingAmount < 0 ? 'expense' : 'refund',
        label: refundPickerTransaction.merchant,
        amount: refundPickerReportingAmount,
        date: refundPickerTransaction.date,
        canFlagExpected: canFlagExpectedRefund(refundPickerTransaction),
        subsequentInstallments: subsequentInstallmentsOf(refundPickerTransaction),
        // What is still unlinked on the subject — the consequence note needs it, since a second
        // partial estorno can COMPLETE an expense that already carries earlier coverage.
        uncoveredAmount: uncoveredAmount(refundPickerTransaction),
        relinkingLinkId: refundPicker?.mode === 'relink'
          ? refundPickerTransaction.refundLink?.linkId
          : undefined,
      }
    : null;

  const openRefundPickerFromRow = (txId: string) => {
    const tx = visibleTransactions.find((candidate) => candidate.id === txId);
    if (!tx || !hasReportingAmount(tx)) return;
    setRefundPicker({ id: txId, source: 'transactions', mode: 'add' });
  };
  const openRefundPickerFromDetail = (txId: string, mode: 'add' | 'relink' = 'add') => {
    const tx = visibleTransactions.find((candidate) => candidate.id === txId)
      ?? (resolvedDetailTx?.id === txId ? resolvedDetailTx : null);
    if (!tx || !hasReportingAmount(tx)) return;
    setRefundPicker({ id: txId, source: 'detail', mode });
  };
  const handleRefundPickerOpenChange = (open: boolean) => {
    if (!open) {
      setRefundPicker(null);
      setReviewPicking(null);
    }
  };

  const mutateReviewQueue = useCallback(
    () => mutateGlobal((key) => Array.isArray(key) && key[0] === 'review-queue'),
    [mutateGlobal],
  );

  const patchRefundPages = (patch: (results: VerticeTransaction[]) => VerticeTransaction[]) =>
    mutateTransactionPages(
      (current) => current?.map((page) => ({
        ...page,
        items: patch(page.items),
      })),
      { revalidate: false },
    );

  const screenTransactionSource = (screenTx: ScreenTransaction) => screenTx.source;
  // The drawer's fallback (detailTx/detailTxOrigin) can hold a row fetched via
  // getOne that no SWR page contains — the pages patch alone would leave it
  // stale forever, so every refund mutation patches these states too.
  const patchDetailStates = (patch: (results: VerticeTransaction[]) => VerticeTransaction[]) => {
    wroteHeldRows();
    setDetailTx((current) =>
      patchStandaloneTransaction(current, screenTransactionSource, patch, adaptTransactionForScreen));
    setDetailTxOrigin((current) =>
      patchStandaloneTransaction(current, screenTransactionSource, patch, adaptTransactionForScreen));
  };

  // Link/unlink patches guess `isCashFlowNeutral` — the client cannot recompute
  // base neutrality (custom-neutral categories are server-side). Page rows heal
  // via revalidation; standalone detail rows never would, so after the mutation
  // settles the authoritative rows are refetched. Best-effort: a failed fetch
  // keeps the optimistic value. A space write's reread passes its `landed`; any
  // other refresh is a write the reread didn't see.
  const refreshStandaloneFromServer = (
    affectedIds: readonly (string | null | undefined)[],
    landed?: () => boolean,
  ) => {
    const ids = heldAffectedIds(affectedIds, [detailTx?.id, detailTxOrigin?.id]);
    if (ids.length === 0) return;
    const settle = landed ? undefined : readingHeldRows();
    void Promise.allSettled(ids.map((id) => authedFetch((token) => transactionsApi.getOne(token, id))))
      .then((results) => {
        const fresh = new Map<string, ScreenTransaction>();
        for (const result of results) {
          if (result.status === 'fulfilled') fresh.set(result.value.id, adaptTransactionForScreen(result.value));
        }
        if (fresh.size === 0) return;
        if (landed && !landed()) return;
        settle?.();
        setDetailTx((current) => (current && fresh.get(current.id)) ?? current);
        setDetailTxOrigin((current) => (current && fresh.get(current.id)) ?? current);
      });
  };

  const handleLinkRefund = async (subjectId: string, candidate: TransactionRefundCandidate) => {
    const picker = refundPicker;
    const subjectTx = refundPickerTransaction;
    if (!subjectTx || subjectTx.id !== subjectId) return;
    const subjectReportingAmount = reportingAmount(subjectTx);
    if (subjectReportingAmount == null) return;

    const subjectSide: RefundPairSide = {
      id: subjectTx.id,
      description: subjectTx.source.description ?? subjectTx.merchant,
      merchantName: subjectTx.source.merchantName ?? null,
      amount: subjectReportingAmount,
      date: subjectTx.date,
      accountName: subjectTx.source.account?.name ?? 'Conta',
    };
    const candidateSide: RefundPairSide = {
      id: candidate.transactionId,
      description: candidate.description,
      merchantName: candidate.merchantName ?? null,
      amount: candidate.amount,
      date: candidate.date,
      accountName: candidate.accountName,
    };
    const isExpenseAnchor = subjectReportingAmount < 0;
    const expenseSide = isExpenseAnchor ? subjectSide : candidateSide;
    const refundSide = isExpenseAnchor ? candidateSide : subjectSide;
    // ADD is the default: only an explicit "Alterar vínculo" re-points the existing link.
    // (`picker` is null on the review-queue path, which always creates a new link.)
    const existingLinkId = picker?.mode === 'relink' ? (subjectTx.refundLink?.linkId ?? null) : null;

    setSavingRefundTransactionId(subjectId);
    try {
      // "Alterar vínculo" replaces only the counterpart side of the existing link.
      const link = existingLinkId
        ? await authedFetch((token) => refundsApi.relink(
            token,
            existingLinkId,
            isExpenseAnchor
              ? { refundTransactionId: refundSide.id }
              : { expenseTransactionId: expenseSide.id },
          ))
        : await authedFetch((token) => refundsApi.link(token, {
            expenseTransactionId: expenseSide.id,
            refundTransactionId: refundSide.id,
          }));
      const patch: RefundLinkPatchInput = {
        linkId: link.id,
        linkedAmount: link.amount,
        createdAt: link.createdAt,
        expense: expenseSide,
        refund: refundSide,
      };
      const applyLinkPatch = (results: VerticeTransaction[]) => applyRefundLinkPatch(
        existingLinkId ? clearRefundLinkPatch(results, existingLinkId) : results,
        patch,
      );
      await patchRefundPages(applyLinkPatch);
      patchDetailStates(applyLinkPatch);
      // On a relink the OLD counterpart also changed (its link was cleared).
      refreshStandaloneFromServer([
        expenseSide.id,
        refundSide.id,
        existingLinkId ? subjectTx.refundLink?.counterpartTransactionId : null,
      ]);
      // Full revalidate as safety — the link endpoints don't return updated rows.
      void mutateTransactionPages();
      void mutateTransactionsMeta();
      void mutateReviewQueue();
      revalidatePostRefundCaches(mutateGlobal);
      revalidateAfterEdit();
      setRefundPicker(null);
      // Coverage is CUMULATIVE now, so this single allocation does not classify the result:
      // adding R$40 to an expense already covered by R$60 completes a R$100 expense. Compute
      // the post-mutation total on the expense (a relink swaps its own old allocation out)
      // and let that drive both the telemetry flag and the toast wording.
      const expenseRow = isExpenseAnchor
        ? subjectTx
        : visibleTransactions.find((t) => t.id === expenseSide.id);
      const priorCoverage = expenseRow ? totalRefundedAmount(expenseRow) : 0;
      const replacedCoverage = existingLinkId
        ? (expenseRow?.refundLinks?.find((l) => l.linkId === existingLinkId)?.linkedAmount ?? 0)
        : 0;
      const coverageAfter = priorCoverage - replacedCoverage + link.amount;
      track('action.refund.link', {
        source: picker?.source ?? 'transactions',
        partial: coverageAfter < Math.abs(expenseSide.amount),
      });
      toast.success(refundLinkToastMessage(expenseSide.amount, coverageAfter));
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível vincular o reembolso');
    } finally {
      setSavingRefundTransactionId(null);
    }
  };

  const relinkAfterUndo = async (
    expenseTransactionId: string,
    refundTransactionId: string,
  ) => {
    try {
      await authedFetch((token) => refundsApi.link(token, { expenseTransactionId, refundTransactionId }));
      refreshStandaloneFromServer([expenseTransactionId, refundTransactionId]);
      void mutateTransactionPages();
      void mutateTransactionsMeta();
      revalidatePostRefundCaches(mutateGlobal);
      revalidateAfterEdit();
      track('action.refund.unlink', { source: 'detail', undo: true });
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível refazer o vínculo');
    }
  };

  const handleUnlinkRefund = async (tx: ScreenTransaction, linkId: string) => {
    // With several counterparts the caller names WHICH link to remove; fall back to the row's
    // first link for callers that still pass none.
    const link = (tx.refundLinks ?? (tx.refundLink ? [tx.refundLink] : []))
      .find((l) => l.linkId === linkId) ?? tx.refundLink;
    if (!link) return;
    const expenseTransactionId = link.role === 'expense' ? tx.id : link.counterpartTransactionId;
    const refundTransactionId = link.role === 'refund' ? tx.id : link.counterpartTransactionId;

    setSavingRefundTransactionId(tx.id);
    try {
      await authedFetch((token) => refundsApi.unlink(token, link.linkId));
      const applyUnlinkPatch = (results: VerticeTransaction[]) => clearRefundLinkPatch(results, link.linkId);
      await patchRefundPages(applyUnlinkPatch);
      patchDetailStates(applyUnlinkPatch);
      refreshStandaloneFromServer([expenseTransactionId, refundTransactionId]);
      void mutateTransactionPages();
      void mutateTransactionsMeta();
      revalidatePostRefundCaches(mutateGlobal);
      revalidateAfterEdit();
      track('action.refund.unlink', { source: 'detail', undo: false });
      // Unlinking a series member removes the WHOLE series server-side (delete cascades from
      // the root). A single-pair re-link could not faithfully rebuild it, so we omit "Desfazer"
      // for series links; a 1:1 link keeps its Undo.
      const undoUnlink = () => void relinkAfterUndo(expenseTransactionId, refundTransactionId);
      // Derived, not assumed: with another estorno still covering part of the expense — or an
      // active previsto flag — it does not return to the pace (or does not return at all).
      toast.success(refundUnlinkToastMessage(tx, link.linkedAmount),
        link.isSeriesLink
          ? undefined
          : { action: { label: 'Desfazer', onClick: undoUnlink } });
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível desvincular o reembolso');
    } finally {
      setSavingRefundTransactionId(null);
    }
  };

  const setExpectedRefundFlag = async (
    tx: ScreenTransaction,
    flagged: boolean,
    undo: boolean,
    source: 'detail' | 'picker' = 'detail',
  ) => {
    if (flagged && !hasReportingAmount(tx)) return;
    setSavingRefundTransactionId(tx.id);
    const flaggedAt = flagged ? new Date().toISOString() : null;
    const previousFlaggedAt = tx.source.expectedRefundFlaggedAt ?? null;
    const applyFlagPatch = (results: VerticeTransaction[]) =>
      applyExpectedRefundPatch(results, tx.id, flaggedAt);
    await patchRefundPages(applyFlagPatch);
    patchDetailStates(applyFlagPatch);
    try {
      const updated = await authedFetch((token) => refundsApi.setExpectedRefund(token, tx.id, flagged));
      const applyServerRow = (results: VerticeTransaction[]) => replaceTransactionById(results, updated);
      await patchRefundPages(applyServerRow);
      patchDetailStates(applyServerRow);
      // Facet membership is server-side: under refund=pending an unflagged row
      // must leave the filtered list, which only a page revalidation can do.
      // Unconditional by choice — cheap, and gating on the active facet would
      // leave other filtered caches stale.
      void mutateTransactionPages();
      void mutateTransactionsMeta();
      revalidatePostRefundCaches(mutateGlobal);
      revalidateAfterEdit();
      track('action.refund.flag_expected', { source, flagged, undo });
      if (!undo) {
        const undoFlag = () => void setExpectedRefundFlag(tx, !flagged, true, source);
        toast.success(
          flagged
            ? 'Reembolso previsto — a despesa sai do seu ritmo de gastos por enquanto'
            : 'Flag removida — a despesa volta ao seu ritmo de gastos',
          { action: { label: 'Desfazer', onClick: undoFlag } },
        );
      }
    } catch (err) {
      // Rollback: a plain revalidate wipes the optimistic patch with server
      // truth; the standalone detail fallback (no page to heal it) is restored
      // by re-applying the pre-mutation flag state.
      void mutateTransactionPages();
      patchDetailStates((results) => applyExpectedRefundPatch(results, tx.id, previousFlaggedAt));
      notifyError('transactions', err, 'Não foi possível atualizar o reembolso previsto');
    } finally {
      setSavingRefundTransactionId(null);
    }
  };

  const handleFlagExpected = (tx: ScreenTransaction, flagged: boolean) => {
    void setExpectedRefundFlag(tx, flagged, false);
  };

  // Picker "Reembolso previsto" toggle: flag instead of linking, then close.
  const handlePickerFlagExpected = async (subjectId: string) => {
    const tx = refundPickerTransaction;
    if (!tx || tx.id !== subjectId) return;
    setRefundPicker(null);
    await setExpectedRefundFlag(tx, true, false, 'picker');
  };

  const openCounterpartDetails = async (counterpartTransactionId: string) => {
    const origin = resolvedDetailTx;
    const live = visibleTransactions.find((t) => t.id === counterpartTransactionId);
    if (live) {
      setDetailTxOrigin(origin);
      setDetailTx(live);
      return;
    }
    const settle = readingHeldRows();
    try {
      const fetched = await authedFetch((token) => transactionsApi.getOne(token, counterpartTransactionId));
      settle();
      const shown = resolvedDetailTxRef.current;
      setDetailTxOrigin(shown && shown.id === origin?.id ? shown : origin);
      setDetailTx(adaptTransactionForScreen(fetched));
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível abrir o lançamento vinculado');
    }
  };

  const returnToOriginDetails = () => {
    setDetailTx(detailTxOrigin);
    setDetailTxOrigin(null);
  };

  // ── Refund review slice ("A revisar" card on this page) ────────────────────

  const dropRefundQueueItem = (item: ReviewQueueItem) =>
    mutateRefundReviewQueue(
      (current) => current
        ? {
            ...current,
            total: Math.max(0, current.total - 1),
            results: current.results.filter((queued) => reviewItemKey(queued) !== reviewItemKey(item)),
          }
        : current,
      { revalidate: false },
    );

  // Optimistically drops ONLY the acted-on (id, reason) item, then refreshes the
  // queue family (home total included) and every flow surface the link touched.
  const finishRefundReviewAction = (item: ReviewQueueItem) => {
    void dropRefundQueueItem(item);
    void mutateReviewQueue();
    void mutateTransactionPages();
    void mutateTransactionsMeta();
    revalidatePostRefundCaches(mutateGlobal);
    revalidateAfterEdit();
  };

  const handleConfirmRefundReview = async (item: ReviewQueueItem) => {
    const suggestion = item.refundSuggestion;
    if (!suggestion) return;
    setReviewBusyKey(reviewItemKey(item));
    try {
      await authedFetch((token) =>
        refundsApi.link(token, resolveRefundPair(item, suggestion.transactionId)),
      );
      track('action.refund.review_resolve', { reason: 'possible_refund', action: 'link' });
      track('action.refund.link', {
        source: 'review_queue',
        partial: Math.abs(item.amount) < Math.abs(suggestion.amount),
      });
      toast.success(refundLinkToastMessage(suggestion.amount, item.amount));
      finishRefundReviewAction(item);
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível vincular o reembolso');
    } finally {
      setReviewBusyKey(null);
    }
  };

  // "Manter como despesa": clears the expired previsto flag, removing only the
  // refund item — a category item for the same transaction stays queued.
  const handleKeepAsExpenseReview = async (item: ReviewQueueItem) => {
    setReviewBusyKey(reviewItemKey(item));
    try {
      await authedFetch((token) => refundsApi.setExpectedRefund(token, item.id, false));
      track('action.refund.review_resolve', { reason: 'refund_not_arrived', action: 'keep_expense' });
      toast.success('Despesa mantida nos totais');
      finishRefundReviewAction(item);
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível confirmar o lançamento');
    } finally {
      setReviewBusyKey(null);
    }
  };

  const handleReviewPickerSave = async (item: ReviewQueueItem, candidate: TransactionRefundCandidate) => {
    const notArrived = item.reason === 'refund_not_arrived';
    setReviewBusyKey(reviewItemKey(item));
    try {
      await authedFetch((token) =>
        refundsApi.link(token, resolveRefundPair(item, candidate.transactionId)),
      );
      track('action.refund.review_resolve', {
        reason: notArrived ? 'refund_not_arrived' : 'possible_refund',
        action: 'link',
      });
      // Compare REMAINING values: a refund_not_arrived expense may already be partly covered,
      // and a candidate estorno may itself be partly spent. Allocation is min(remainder,
      // available), so the expense completes whenever the estorno can supply the remainder.
      const candidateAvailable = candidate.availableAmount ?? Math.abs(candidate.amount);
      const expenseRemainder = notArrived
        ? item.expiredRefund?.uncoveredAmount ?? Math.abs(item.amount)
        : candidateAvailable;
      const refundAvailable = notArrived ? candidateAvailable : Math.abs(item.amount);
      track('action.refund.link', {
        source: 'review_queue',
        partial: refundAvailable < expenseRemainder,
      });
      toast.success(refundLinkToastMessage(expenseRemainder, refundAvailable));
      setReviewPicking(null);
      finishRefundReviewAction(item);
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível vincular o reembolso');
    } finally {
      setReviewBusyKey(null);
    }
  };

  // "Não é reembolso — manter como entrada": dismisses ONLY the refund proposal;
  // a category review item for the same transaction stays in its queue. Reachable
  // directly from the review row and from the picker's escape hatch.
  const handleDismissRefundReview = async (item: ReviewQueueItem): Promise<boolean> => {
    if (item.reason !== 'possible_refund') return false;
    setReviewBusyKey(reviewItemKey(item));
    try {
      await authedFetch((token) => refundsApi.dismissRefundSuggestion(token, item.id));
      track('action.refund.review_resolve', { reason: 'possible_refund', action: 'not_refund' });
      toast.success('Mantido como entrada');
      finishRefundReviewAction(item);
      return true;
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível confirmar o lançamento');
      return false;
    } finally {
      setReviewBusyKey(null);
    }
  };

  // Picker escape hatch: keep the picker open on failure so its context survives a retry.
  const handleNotARefundReview = async () => {
    const item = reviewPicking;
    if (!item) return;
    if (await handleDismissRefundReview(item)) setReviewPicking(null);
  };

  const reviewPickingSubject: RefundPickerSubject | null = reviewPicking
    ? {
        id: reviewPicking.id,
        side: reviewPicking.reason === 'refund_not_arrived' ? 'expense' : 'refund',
        label: reviewPicking.merchant ?? reviewPicking.description,
        amount: reviewPicking.amount,
        date: reviewPicking.date,
        // A refund_not_arrived expense may already be PARTLY refunded (a partial estorno no
        // longer blocks the previsto flag), so the consequence copy must compare against what
        // is left, not the face amount. possible_refund inflows are unlinked by definition.
        uncoveredAmount: reviewPicking.expiredRefund?.uncoveredAmount,
      }
    : null;
  const pickerSubject = refundPickerSubject ?? reviewPickingSubject;

  const handlePickerSave = (subjectId: string, candidate: TransactionRefundCandidate) => {
    if (reviewPicking && reviewPicking.id === subjectId) {
      return handleReviewPickerSave(reviewPicking, candidate);
    }
    return handleLinkRefund(subjectId, candidate);
  };

  const selectedFatura = selectedFaturaId ? faturaById.get(selectedFaturaId) ?? null : null;

  // A space write changes what a row says it was sent to (`spaceExpenses`, VER-571): the drawer's rows held outside the
  // list are read again, and so are the open invoice's charges, in place, unless a newer reread or another read of
  // those rows landed meanwhile.
  const { wrote: wroteHeldRows, reading: readingHeldRows } = useRereadOnSpaceWrite(
    (landed) => refreshStandaloneFromServer([detailTx?.id, detailTxOrigin?.id], landed),
  );
  const { wrote: wroteFaturaDetail, reading: readingFaturaDetail } = useRereadOnSpaceWrite((landed) => {
    if (!selectedFatura) return;
    const { accountId, source } = selectedFatura;
    const target = parseInvoiceDetailTarget(selectedFatura);
    const readPage: InvoicePageRead = (page) =>
      fetchInvoiceDetail(accountId, target.year, target.month, source.position, page);
    reloadInvoiceDetail(readPage, faturaDetail, INVOICE_PAGE_SIZE)
      .then((res) => {
        if (landed()) setFaturaDetail((current) => invoiceAfterReread(current, res));
      })
      .catch((err) => console.error('Fatura transactions reload failed', err));
  });

  // Fetch the fatura's authoritative detail payload on demand. This avoids the
  // transaction-list endpoint entirely, so the drawer cannot be capped by the
  // Transactions page pagination or by the current visible row count.
  useEffect(() => {
    wroteFaturaDetail();
    if (!selectedFatura) {
      setFaturaDetail(null);
      setIsFaturaDetailLoading(false);
      return;
    }
    let alive = true;
    const target = parseInvoiceDetailTarget(selectedFatura);
    setFaturaDetail(null);
    setIsFaturaDetailLoading(true);
    const settle = readingFaturaDetail();
    fetchInvoiceDetail(
      selectedFatura.accountId,
      target.year,
      target.month,
      selectedFatura.source.position,
      { limit: INVOICE_PAGE_SIZE, offset: 0 },
    )
      .then((res) => {
        if (!alive) return;
        settle();
        setFaturaDetail(res);
      })
      // The detail was cleared when the read started; a reread that landed since keeps what it brought.
      .catch((err) => console.error('Fatura transactions load failed', err))
      .finally(() => {
        if (alive) setIsFaturaDetailLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [
    selectedFatura,
    fetchInvoiceDetail,
    readingFaturaDetail,
    wroteFaturaDetail,
  ]);

  const selectedFaturaTxs = useMemo(
    () => faturaDetail?.transactions.items.map(adaptExpenseTransaction) ?? [],
    [faturaDetail],
  );

  const handleLoadMoreFaturaTransactions = async () => {
    const nextOffset = faturaDetail?.transactions.nextOffset;
    if (!selectedFatura || !faturaDetail?.transactions.hasMore || nextOffset == null) {
      return;
    }
    const target = parseInvoiceDetailTarget(selectedFatura);
    setIsFaturaLoadingMore(true);
    const settle = readingFaturaDetail();
    try {
      const next = await fetchInvoiceDetail(
        selectedFatura.accountId,
        target.year,
        target.month,
        selectedFatura.source.position,
        { limit: INVOICE_PAGE_SIZE, offset: nextOffset },
      );
      settle();
      setFaturaDetail((current) => appendInvoiceDetailPage(current, next));
    } catch (err) {
      console.error('Fatura next page load failed', err);
      notifyError('transactions', err, 'Não foi possível carregar mais lançamentos');
    } finally {
      setIsFaturaLoadingMore(false);
    }
  };

  const handleMarkFaturaPaid = async (faturaId: string) => {
    const fatura = faturaById.get(faturaId);
    // Mirrors the drawer's gate: only closed cycles can be confirmed as paid.
    if (!fatura || fatura.source.isOpen || isMarkingFaturaPaid) return;

    const target = parseInvoiceDetailTarget(fatura);
    setIsMarkingFaturaPaid(true);
    try {
      await markClosedInvoicePaid(
        fatura.accountId,
        target.year,
        target.month,
        fatura.source.dueDate ?? fatura.dueDate,
        fatura.source.chargeFingerprint ?? '',
      );
      // Optimistic flip; the follow-up revalidation reads the live cockpit, which
      // already reflects the persisted override.
      const key = toInvoicePaidKey(fatura);
      await cockpitRequest.mutate(
        (current) => current ? markInvoicePaidInCockpit(current, key) : current,
        { revalidate: false },
      );
      setSelectedFaturaId(null);
      toast.success('Fatura marcada como paga');
      void revalidateCreditCockpit();
    } catch (err) {
      // 409: charges synced after the drawer loaded — refresh so the user sees them.
      const conflict = err instanceof ApiError && err.status === 409;
      if (conflict) void revalidateCreditCockpit();
      notifyError('transactions', conflict ? null : err, conflict
        ? 'A fatura mudou desde que você a abriu. Revise os lançamentos e tente novamente.'
        : 'Não foi possível marcar a fatura como paga');
    } finally {
      setIsMarkingFaturaPaid(false);
    }
  };

  const faturaDrawerCategoryIndex = useMemo(() => {
    const idx = new Map(categoryIndex);
    if (faturaDetail) {
      for (const tx of faturaDetail.transactions.items) {
        const id = tx.categoryId || 'uncategorized';
        if (!idx.has(id)) {
          idx.set(id, {
            id,
            label: tx.categoryTranslated || tx.effectiveCategory || tx.category || tx.parentCategoryTranslated || tx.parentCategory || 'Outros',
            iconName: tx.categoryIcon || tx.parentCategoryIcon || 'circle-dashed',
            color: resolveCategoryColor(id),
          });
        }
        const parentId = tx.parentCategoryId || tx.categoryId || 'uncategorized';
        if (!idx.has(parentId)) {
          idx.set(parentId, {
            id: parentId,
            label: tx.parentCategoryTranslated || tx.parentCategory || tx.categoryTranslated || tx.effectiveCategory || 'Outros',
            iconName: tx.parentCategoryIcon || tx.categoryIcon || 'circle-dashed',
            color: resolveCategoryColor(parentId),
          });
        }
      }
    }
    idx.set(UNCATEGORIZED_META.id, UNCATEGORIZED_META);
    return idx;
  }, [categoryIndex, faturaDetail]);

  /*
  const selectedFatura = selectedFaturaId ? faturaById.get(selectedFaturaId) ?? null : null;
  const selectedFaturaTxs = useMemo(() => {
    if (!selectedFatura) return [];
    const billId = selectedFatura.source.billId;
    if (billId) {
      return adapted.filter((t) => t.source.billId === billId);
    }
    // Open cycle: bill id not yet assigned — fall back to account + cycle range.
    const start = selectedFatura.cycleStart ? new Date(selectedFatura.cycleStart).getTime() : -Infinity;
    const end = selectedFatura.cycleEnd ? new Date(selectedFatura.cycleEnd).getTime() : Infinity;
    return adapted.filter((t) => {
      if (t.accountId !== selectedFatura.accountId) return false;
      const ts = new Date(t.date).getTime();
      return ts >= start && ts <= end;
    });
  }, [selectedFatura, adapted]);
  */

  // Live-count callback for the Tag drawer's Apply CTA. A row can carry several of the
  // selected tags, so the facet sum over-counts — cap at the scope total.
  const previewTagCount = (pending: readonly string[]): number => {
    const total = transactionsMeta?.total ?? 0;
    if (pending.length === 0) return total;
    const sum = pending.reduce((acc, id) => acc + (tagCountsByTagId.get(id) ?? 0), 0);
    return Math.min(total, sum);
  };

  const tagAssignSubject = useMemo<TagAssignSubject | null>(() => {
    if (!tagPickerTransactionId) return null;
    // The paginated list first; then the drawer's standalone fallbacks — a detail row
    // fetched via getOne (refund counterpart) is in NO page, and without this the tag
    // drawer would silently never open for it.
    const raw = transactions.find((t) => t.id === tagPickerTransactionId)
      ?? (detailTx?.id === tagPickerTransactionId ? detailTx.source : null)
      ?? (detailTxOrigin?.id === tagPickerTransactionId ? detailTxOrigin.source : null);
    if (!raw) return null;
    return {
      id: raw.id,
      label: raw.customName ?? raw.merchantName ?? raw.aiRefinedDescription ?? raw.description,
      currentTagIds: (raw.tags ?? []).map((tag) => tag.id),
    };
  }, [tagPickerTransactionId, transactions, detailTx, detailTxOrigin]);

  const handleCreateTag = useCallback(async (name: string, viaFilter: boolean): Promise<TransactionTag> => {
    try {
      const created = await authedFetch((token) => tagsApi.create(token, { name }));
      revalidateTagCatalog(mutateGlobal);
      track('action.tag.create', { via_filter: viaFilter });
      return created;
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível criar a tag');
      throw err;
    }
  }, [authedFetch, mutateGlobal]);

  // Single tag/rename edits return the refreshed row; the SWR pages patch alone would
  // leave a getOne-held drawer fallback (refund counterpart) stale forever — same
  // reason every refund mutation runs patchDetailStates.
  const patchStandaloneFromRow = (updated: VerticeTransaction) => {
    wroteHeldRows();
    const adapted = adaptTransactionForScreen(updated);
    setDetailTx((current) => (current?.id === updated.id ? adapted : current));
    setDetailTxOrigin((current) => (current?.id === updated.id ? adapted : current));
  };

  const handlePreviewTagBatch = useCallback(
    (subjectId: string, tagIds: string[]) => authedFetch(
      (token) => transactionsApi.previewTagBatch(token, { sourceTransactionId: subjectId, tagIds }),
    ),
    [authedFetch],
  );

  const handleSaveTags = async (tagIds: string[], applyBatch: boolean) => {
    const txId = tagPickerTransactionId;
    if (!txId) return;
    setSavingTagsTransactionId(txId);
    try {
      if (applyBatch) {
        const result = await authedFetch(
          (token) => transactionsApi.applyTagBatch(token, { sourceTransactionId: txId, tagIds }),
        );
        void mutateTransactionPages();
        void mutateTransactionsMeta();
        revalidateAfterEdit();
        refreshStandaloneFromServer(result.updatedTransactionIds);
        track('action.tag.assign', { source: 'transactions', mode: 'batch_same_description', count: result.updatedCount });
        toast.success(result.updatedCount === 1 ? '1 lançamento atualizado' : `${result.updatedCount} lançamentos atualizados`);
      } else {
        const updated = await authedFetch((token) => transactionsApi.setTags(token, txId, tagIds));
        await mutateTransactionPages(
          (current) => current?.map((page) => ({
            ...page,
            items: replaceTransactionsById(page.items, [updated]),
          })),
          { revalidate: false },
        );
        patchStandaloneFromRow(updated);
        // The optimistic patch edits the row in place, but the edit can change QUERY
        // membership/ordering (an active tag filter the row no longer matches; merchant
        // sort or a search hitting the custom name) — the server pass restores those.
        void mutateTransactionPages();
        void mutateTransactionsMeta();
        revalidateAfterEdit();
        track('action.tag.assign', { source: 'transactions', mode: 'single', count: 1 });
        toast.success('Tags atualizadas');
      }
      setTagPickerTransactionId(null);
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível salvar as tags');
    } finally {
      setSavingTagsTransactionId(null);
    }
  };

  const handlePreviewRenameBatch = useCallback(
    // The batch population is name-independent — preview with the neutral clear payload.
    (txId: string) => authedFetch(
      (token) => transactionsApi.previewRenameBatch(token, { sourceTransactionId: txId, name: null }),
    ),
    [authedFetch],
  );

  const handleRename = async (txId: string, name: string | null, applyBatch: boolean) => {
    setIsRenameSaving(true);
    try {
      if (applyBatch) {
        const result = await authedFetch(
          (token) => transactionsApi.applyRenameBatch(token, { sourceTransactionId: txId, name }),
        );
        void mutateTransactionPages();
        void mutateTransactionsMeta();
        revalidateAfterEdit();
        refreshStandaloneFromServer(result.updatedTransactionIds);
        track('action.transaction.rename', { source: 'transactions', mode: 'batch_same_description', count: result.updatedCount, cleared: name === null });
        toast.success(result.updatedCount === 1 ? '1 lançamento renomeado' : `${result.updatedCount} lançamentos renomeados`);
      } else {
        const updated = await authedFetch((token) => transactionsApi.rename(token, txId, name));
        await mutateTransactionPages(
          (current) => current?.map((page) => ({
            ...page,
            items: replaceTransactionsById(page.items, [updated]),
          })),
          { revalidate: false },
        );
        patchStandaloneFromRow(updated);
        // The optimistic patch edits the row in place, but the edit can change QUERY
        // membership/ordering (an active tag filter the row no longer matches; merchant
        // sort or a search hitting the custom name) — the server pass restores those.
        void mutateTransactionPages();
        void mutateTransactionsMeta();
        revalidateAfterEdit();
        track('action.transaction.rename', { source: 'transactions', mode: 'single', count: 1, cleared: name === null });
        toast.success(name === null ? 'Nome original restaurado' : 'Nome atualizado');
      }
      setDetailStartInRename(false);
    } catch (err) {
      notifyError('transactions', err, 'Não foi possível renomear o lançamento');
    } finally {
      setIsRenameSaving(false);
    }
  };

  const handleRowRename = (txId: string) => {
    openTransactionDetailsById(txId);
    setDetailStartInRename(true);
  };

  // Live-count callback for the Category drawer's Apply CTA.
  const previewCount = (pending: readonly string[]): number => {
    if (pending.length === 0) return transactionsMeta?.total ?? 0;
    const selected = new Set(pending);
    return (transactionsMeta?.categoryFacets ?? [])
      .filter((facet) => selected.has(facet.parentCategoryId ?? facet.id) || selected.has(facet.id))
      .reduce((sum, facet) => sum + facet.count, 0);
  };

  // Re-group with the fatura-id-augmented transactions so the row pill resolves.
  const groupedSections = useMemo(
    () => groupByDay(visibleTransactions, state.sort),
    [visibleTransactions, state.sort],
  );

  const sectionsWithDates = useMemo(() => withDayTotals(groupedSections), [groupedSections]);

  const hasFilters =
    state.q.trim().length > 0 ||
    state.categoryIds.length > 0 ||
    state.tagIds.length > 0 ||
    state.account !== null ||
    state.paymentMethod !== null ||
    state.platform !== null ||
    state.direction !== null ||
    state.recurrence.length > 0 ||
    state.refund !== null;

  const totalCount = transactionsMeta?.total ?? lastTransactionPage?.total ?? visibleTransactions.length;
  const shownCount = visibleTransactions.length;
  const hasMore = Boolean(lastTransactionPage?.hasMore);
  const isLoadingMore = isTransactionsValidating && shownCount > 0;

  return (
    <div className="space-y-6 pb-20">
      <PageHeader title="Transações" titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl" />

      {isInitialPageLoading ? (
        <TransactionsOverviewSkeleton />
      ) : (
      <div data-tokens="v2" className="space-y-4 sm:space-y-5">
      {/* v2 opt-in: redesign tokens (amount-*, shadow-1, radius-2xl) resolve only inside this scope —
          without it they silently fall back (invisible dots, no shadow). The page migrates to v2
          section by section as it's restyled. */}
      <div data-testid="transactions-summary-row" className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        <TotalCard
          variant="income"
          amount={summary.income}
          count={summary.incomeCount}
          series={summary.dailySeries.map((d) => d.income)}
          periodDays={periodDays}
        />
        <TotalCard
          variant="expense"
          amount={summary.expense}
          count={summary.expenseCount}
          series={summary.dailySeries.map((d) => d.expense)}
          periodDays={periodDays}
        />
        <TotalCard
          variant="net"
          amount={summary.net}
          series={summary.dailySeries.map((d) => d.income - d.expense)}
          periodDays={periodDays}
          incomeForRate={summary.income}
          className="col-span-2 lg:col-span-1"
        />
      </div>

      <RefundReviewCard
        queue={refundReviewQueue}
        confirmingId={reviewBusyKey}
        onConfirmRefund={handleConfirmRefundReview}
        onAlterRefund={setReviewPicking}
        onKeepAsExpense={handleKeepAsExpenseReview}
        onDismissRefund={handleDismissRefundReview}
      />

      <CategoryBreakdownCard
        breakdown={breakdownTop5}
        categoryIndex={categoryIndex}
        selectedCategoryIds={state.categoryFilterMode === 'parent' ? state.categoryIds : []}
        categoryFilterIdByCategoryId={categoryFilterIdByCategoryId}
        onSelectCategory={handleSelectChartCategory}
        onSeeAll={() => setActiveDrawer('category')}
      />
      </div>
      )}

      <div data-testid="transactions-twopane" data-tokens="v2" className="space-y-4 lg:grid lg:grid-cols-[17rem_1fr] lg:items-start lg:gap-6 lg:space-y-0">
      {/* lg:top-20 (not top-4) so the sticky aside clears the 64px sticky header — otherwise the
          first item (the relocated period picker) is occluded on scroll. */}
      <aside className="space-y-4 lg:sticky lg:top-20">
      {/* VER-260: the period control lives with the filters here; the header instance is hidden
          route-level in transactions/page.tsx. */}
      <PeriodPicker value={periodValue} onChange={onPeriodChange} surface="transactions" align="start" />

      <SearchBar value={state.q} onChange={(v) => state.patch({ q: v || null })} />

      <FilterChipsRow
        categoryIds={state.categoryIds}
        tagIds={state.tagIds}
        tags={tagCatalog}
        account={state.account}
        paymentMethod={state.paymentMethod}
        platform={state.platform}
        direction={state.direction}
        recurrence={state.recurrence}
        refund={state.refund}
        accounts={accountOptions}
        categoryIndex={categoryIndex}
        onOpen={(kind) => setActiveDrawer(kind)}
        onClearCategories={() => state.patch({ categoryIds: null })}
        onClearTags={() => state.patch({ tagIds: null })}
        onClearAccount={() => state.patch({ account: null })}
        onClearPaymentMethod={() => state.patch({ paymentMethod: null })}
        onClearPlatform={() => state.patch({ platform: null })}
        onClearDirection={() => state.patch({ direction: null })}
        onClearRecurrence={() => state.patch({ recurrence: null })}
        onClearRefund={handleClearRefundFacet}
      />

      <SortControl
        value={state.sort}
        order={state.order}
        onChange={(v) => state.patch({ sort: v })}
        onToggleOrder={() => state.patch({ order: state.order === 'desc' ? 'asc' : 'desc' })}
      />
      </aside>

      <div className="space-y-6 lg:min-w-0 lg:space-y-0">
      {isInitialPageLoading && (
        <TransactionListCard>
          <ul className="divide-y divide-border" role="status" aria-label="Carregando transações">
            <li><TransactionRowSkeleton /></li>
            <li><TransactionRowSkeleton /></li>
            <li><TransactionRowSkeleton /></li>
          </ul>
        </TransactionListCard>
      )}
      {!isInitialPageLoading && visible.length === 0 && (
        <TransactionListEmpty
          icon={ReceiptIcon}
          title={hasFilters ? 'Nenhuma transação corresponde aos filtros' : 'Nenhuma transação no período'}
          action={hasFilters && (
            <button
              type="button"
              onClick={() => state.patch({ q: null, categoryIds: null, tagIds: null, account: null, paymentMethod: null, platform: null, direction: null, recurrence: null, refund: null })}
              className="mt-3 text-sm font-medium text-primary hover:underline"
            >
              Limpar filtros
            </button>
          )}
        />
      )}
      {!isInitialPageLoading && visible.length > 0 && (
        <TransactionListCard
          label="Lançamentos"
          count={totalCount === 0
            ? '0'
            : hasMore
              ? `${shownCount} de ${totalCount}`
              : `${totalCount}`}
          footer={hasMore && (
            <button
              type="button"
              onClick={() => setTransactionPageCount(transactionPageCount + 1)}
              disabled={isLoadingMore}
              className="flex h-11 w-full items-center justify-center rounded-xl bg-surface-sunken text-sm font-medium text-text-primary transition hover:bg-accent active:scale-[0.99]"
            >
              {isLoadingMore ? 'Carregando...' : `Carregar mais (${Math.max(totalCount - shownCount, 0)} restantes)`}
            </button>
          )}
        >
          {sectionsWithDates.map((section) => (
            <div key={section.key}>
              {section.date && <DayHeader date={section.date} total={section.total} />}
              <ul className="divide-y divide-border">
                {section.transactions.map((tx) => (
                  <li key={tx.id}>
                    <TransactionRow
                      tx={tx as ScreenTransaction}
                      expanded={expandedRowId === tx.id}
                      onToggle={() => setExpandedRowId((cur) => (cur === tx.id ? null : tx.id))}
                      onOpenDetails={openTransactionDetailsById}
                      faturaById={faturaById}
                      onOpenFatura={(faturaId) => {
                        track('action.fatura.open_detail', { source: 'transactions' });
                        setSelectedFaturaId(faturaId);
                      }}
                      onOpenCategoryPicker={setCategoryPickerTransactionId}
                      onOpenTagPicker={setTagPickerTransactionId}
                      onRename={handleRowRename}
                      onMarkRecurring={handleMarkTransactionRecurring}
                      onOpenRefundLink={openRefundPickerFromRow}
                      isCategorySaving={savingCategoryTransactionId === tx.id}
                      isTagSaving={savingTagsTransactionId === tx.id}
                      isRecurringSaving={savingRecurringTransactionId === tx.id}
                      isRefundSaving={savingRefundTransactionId === tx.id}
                      categoryIndex={categoryIndex}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </TransactionListCard>
      )}
      </div>
      </div>

      <MarkRecurringSheet
        open={markRecurringTarget !== null}
        onOpenChange={(open) => {
          if (!open) setMarkRecurringTarget(null);
        }}
        merchant={markRecurringTarget?.merchant ?? null}
        defaultKind={markRecurringTarget?.defaultKind ?? 'Utility'}
        saving={savingRecurringTransactionId !== null}
        onConfirm={confirmMarkTransactionRecurring}
      />
      <AccountPickerDrawer
        open={activeDrawer === 'account'}
        onOpenChange={(o) => setActiveDrawer(o ? 'account' : null)}
        value={state.account}
        accounts={filteredAccountOptions}
        onSelect={(v) => state.patch({ account: v })}
      />
      <PaymentMethodPickerDrawer
        open={activeDrawer === 'paymentMethod'}
        onOpenChange={(o) => setActiveDrawer(o ? 'paymentMethod' : null)}
        value={state.paymentMethod}
        onSelect={(v) => state.patch({ paymentMethod: v })}
        availableMethods={availablePaymentMethods}
      />
      <PlatformPickerDrawer
        open={activeDrawer === 'platform'}
        onOpenChange={(o) => setActiveDrawer(o ? 'platform' : null)}
        value={state.platform}
        onSelect={(v) => state.patch({ platform: v })}
        facetCounts={platformFacetCounts}
      />
      <DirectionPickerDrawer
        open={activeDrawer === 'direction'}
        onOpenChange={(o) => setActiveDrawer(o ? 'direction' : null)}
        value={state.direction}
        onSelect={(v) => state.patch({ direction: v })}
      />
      <RecurrencePickerDrawer
        open={activeDrawer === 'recurrence'}
        onOpenChange={(o) => setActiveDrawer(o ? 'recurrence' : null)}
        value={state.recurrence}
        onChange={(next) => state.patch({ recurrence: next.length > 0 ? next : null })}
      />
      <RefundFacetPickerDrawer
        open={activeDrawer === 'refund'}
        onOpenChange={handleRefundDrawerOpenChange}
        value={state.refund}
        onSelect={handleSelectRefundFacet}
      />
      <TagFilterDrawer
        open={activeDrawer === 'tag'}
        onOpenChange={(o) => setActiveDrawer(o ? 'tag' : null)}
        value={state.tagIds}
        onApply={(ids) => state.patch({ tagIds: ids.length > 0 ? ids : null })}
        previewCount={previewTagCount}
        tags={tagCatalog}
        countsByTagId={tagCountsByTagId}
        amountsByTagId={tagAmountsByTagId}
        onCreateTag={(name) => handleCreateTag(name, true)}
      />
      <TagAssignDrawer
        open={tagAssignSubject != null}
        onOpenChange={(o) => setTagPickerTransactionId(o ? tagPickerTransactionId : null)}
        subject={tagAssignSubject}
        tags={tagCatalog}
        saving={savingTagsTransactionId != null}
        onSave={handleSaveTags}
        onCreateTag={(name) => handleCreateTag(name, false)}
        onPreviewBatch={handlePreviewTagBatch}
      />
      <CategoryFilterDrawer
        open={activeDrawer === 'category'}
        onOpenChange={(o) => setActiveDrawer(o ? 'category' : null)}
        value={state.categoryFilterMode === 'parent' ? state.categoryIds : EMPTY_CATEGORY_FILTER}
        onApply={(ids) => state.patch({
          categoryIds: ids.length > 0 ? ids : null,
          categoryFilterMode: ids.length > 0 ? 'parent' : null,
        })}
        previewCount={previewCount}
        sharesByCategoryId={sharesByCategoryId}
        amountsByCategoryId={amountsByCategoryId}
        availableCategoryIds={availableCategoryIds}
        recentCategoryIds={state.recentCategoryIds}
        categoryIndex={categoryIndex}
        dateRange={state.dateRange}
        onApplyParentCategoryFilter={(id) => {
          const filterId = categoryFilterIdByCategoryId.get(id) ?? id;
          state.patch({ categoryIds: [filterId], categoryFilterMode: 'parent' });
          setActiveDrawer(null);
        }}
        onApplyChildCategoryFilter={(id) => {
          state.patch(exactCategoryFilterPatch(id));
          setActiveDrawer(null);
        }}
        // The category drawer stays open underneath — the detail drawer stacks on
        // top, so its back arrow (and any close gesture) lands back on the category
        // context instead of the bare page. The full transaction is stored as-is;
        // resolvedDetailTx re-resolves it live at render.
        onOpenTransactionDetails={(tx) => {
          if (tx) track('action.transaction.open_detail', { source: 'category_detail' });
          wroteHeldRows();
          setDetailTx(tx);
        }}
      />
      <TransactionDetailDrawer
        open={detailTx != null}
        onOpenChange={(o) => (o ? setDetailTx(detailTx) : closeTransactionDetails())}
        onBack={detailTxOrigin
          ? returnToOriginDetails
          : activeDrawer === 'category' ? closeTransactionDetails : undefined}
        tx={resolvedDetailTx}
        faturaById={faturaById}
        onOpenFatura={(id) => {
          track('action.fatura.open_detail', { source: 'transactions' });
          closeTransactionDetails();
          setSelectedFaturaId(id);
        }}
        categoryIndex={categoryIndex}
        onOpenCounterpart={openCounterpartDetails}
        onOpenRefundLink={openRefundPickerFromDetail}
        onUnlinkRefund={handleUnlinkRefund}
        onFlagExpected={handleFlagExpected}
        isRefundSaving={resolvedDetailTx != null && savingRefundTransactionId === resolvedDetailTx.id}
        onEditTags={setTagPickerTransactionId}
        onRename={handleRename}
        onPreviewRenameBatch={handlePreviewRenameBatch}
        isRenameSaving={isRenameSaving}
        startInRename={detailStartInRename}
      />
      <RefundLinkPickerDrawer
        open={refundPicker != null || reviewPicking != null}
        onOpenChange={handleRefundPickerOpenChange}
        subject={pickerSubject}
        saving={savingRefundTransactionId != null || reviewBusyKey != null}
        onSave={handlePickerSave}
        onNotARefund={reviewPicking?.reason === 'possible_refund' ? handleNotARefundReview : undefined}
        onFlagExpected={handlePickerFlagExpected}
      />
      <CategoryPickerDrawer
        open={!!categoryPickerTransactionId}
        onOpenChange={(o) => setCategoryPickerTransactionId(o ? categoryPickerTransactionId : null)}
        subject={categoryPickerSubject}
        categoryIndex={categoryIndex}
        saving={!!savingCategoryTransactionId}
        onSave={handleSaveTransactionCategory}
        sameDescriptionBatch={{
          onPreview: handlePreviewTransactionCategoryBatch,
          onSave: handleSaveTransactionCategoryBatch,
          anchors: categoryPickerAnchors,
          anchorsPending: categoryPickerAnchorsPending,
        }}
        ruleCreation={categoryPickerRuleConfig.ruleCreation}
      />
      <FaturaDetailDrawer
        open={!!selectedFaturaId}
        onOpenChange={(o) => setSelectedFaturaId(o ? selectedFaturaId : null)}
        fatura={selectedFatura}
        transactions={selectedFaturaTxs}
        categoryIndex={faturaDrawerCategoryIndex}
        onMarkAsPaid={handleMarkFaturaPaid}
        isMarkingPaid={isMarkingFaturaPaid}
        isLoading={isFaturaDetailLoading}
        totalCount={faturaDetail?.transactions.total ?? selectedFatura?.transactionCount ?? selectedFaturaTxs.length}
        hasMore={Boolean(faturaDetail?.transactions.hasMore)}
        isLoadingMore={isFaturaLoadingMore}
        onLoadMore={handleLoadMoreFaturaTransactions}
      />
    </div>
  );
}
